<template>
  <AutoComplete
      ref="searchInputRef"
      v-model="searchString"
      :suggestions="filteredSuggestions"
      option-group-label="name"
      option-group-children="items"
      optionLabel="ticker"
      @complete="onSearch"
      @item-select="handleSelection"
      placeholder="Search ticker or name"
      input-class="w-full placeholder:text-md placeholder:font-light"
      variant="filled"
      panel-class="shadow-xl shadow-black/20"
      scroll-height="100vh"
      pt:optiongroup:class="text-primary font-bold tracking-wide border-b mt-5 first:mt-0 mb-3"
  >
    <template #option="slotProps">
      <div class="flex justify-between w-full gap-2">
        <div class="max-w-64 truncate">{{ slotProps.option.full_name }}</div>
        <div class="font-semibold">{{ slotProps.option.ticker }}</div>
      </div>
    </template>
  </AutoComplete>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useTickersStore } from "@/stores/Tickers";
import { useSectorsStore } from "@/stores/Sectors";
import { useDebounceFn } from "@vueuse/core";
import { useFuse } from "@vueuse/integrations/useFuse";

import { AutoCompleteOptionSelectEvent } from "primevue/autocomplete";
import { IClickHandler } from "@/types/handlers/click-handler";
import type { ContentfulSector, SearchableTicker } from "@/types/entities";
import { usePermissionsStore } from "@/stores/Permissions";
import { storeToRefs } from "pinia";
import { reportSearchEvent } from "@/composables/user-activity-monitor";

const { hasEtfAccess, hasSectorAccess, hasStockAccess } = storeToRefs( usePermissionsStore() );

type SearchResult = {
  full_name: string,
  ticker: string,
  isStock?: boolean,
  isEtf?: boolean,
  isSector?: boolean,
};

type SearchGroupResult = {
  name: string,
  items: SearchResult[];
};

type FuseResultTickerItem = {
  item: SearchableTicker;
  refIndex: number;
};

type FuseResultSectorItem = {
  item: ContentfulSector;
  refIndex: number;
};

export interface ISearchProps {
  supportsStocks?: boolean,
  stockClickHandler?: IClickHandler,
  supportsEtfs?: boolean,
  etfClickHandler?: IClickHandler,
  supportsSectors?: boolean,
  sectorClickHandler?: IClickHandler,
}

const props = withDefaults( defineProps< ISearchProps >(), {
  supportsStocks: true,
  supportsEtfs: true,
  supportsSectors: true
} );

const tickersStore = useTickersStore();
const sectorsStore = useSectorsStore();

const searchInputRef = ref();

const searchString = ref< string >( '' );
const filteredSuggestions = ref< SearchGroupResult[] >( [] );

const fuseConfig = {
  resultLimit: 10,
  fuseOptions: {
    shouldSort: true,
    ignoreLocation: true,
    isCaseSensitive: false,
    threshold: 0.3,
    keys: [
      { name: "name", weight: 0.3 },
      { name: "ticker", weight: 0.7 },
      { name: "symbol", weight: 0.7 },
    ]
  },
};

const debouncedSearch = useDebounceFn( () => {
  // Clear out results container
  filteredSuggestions.value.splice( 0 );

  // Normalize the search string
  const originalSearchString: string = searchString.value.toLocaleLowerCase();

  if ( hasStockAccess.value && props.supportsStocks ) {
    const { results } = useFuse( originalSearchString, tickersStore.searchableStocks, fuseConfig );
    if ( results.value.length > 0 ) {
      filteredSuggestions.value.push( {
        name: 'Stocks',
        items: [ ...results.value.map( ( stock: FuseResultTickerItem ) => ( {
          full_name: stock.item.name,
          ticker: stock.item.ticker,
          isStock: true
        } ) ) ]
      } );
    }
  }

  if ( hasEtfAccess.value && props.supportsEtfs ) {
    const { results } = useFuse( originalSearchString, tickersStore.searchableEtfs, fuseConfig );
    if ( results.value.length > 0 ) {
      filteredSuggestions.value.push( {
        name: 'ETFs',
        items: [ ...results.value.map( ( stock: FuseResultTickerItem ) => ( {
          full_name: stock.item.name,
          ticker: stock.item.ticker,
          isEtf: true
        } ) ) ]
      } );
    }
  }

  if ( hasSectorAccess.value && props.supportsSectors ) {
    const { results } = useFuse( originalSearchString, sectorsStore.allSectors, fuseConfig );
    if ( results.value.length > 0 ) {
      filteredSuggestions.value.push( {
        name: 'Sectors',
        items: [ ...results.value.map( ( sector: FuseResultSectorItem ) => ( {
          full_name: sector.item.name,
          ticker: sector.item.symbol,
          isSector: true
        } ) ) ]
      } );
    }
  }

  searchInputRef.value.overlayVisible = true;
  searchInputRef.value.searching = false;
}, 350, { maxWait: 5000 } );

function onSearch() {
  if ( !searchString.value ) {
    return;
  }

  debouncedSearch();
}

function handleSelection( selectedItem: AutoCompleteOptionSelectEvent ) {
  searchString.value = '';
  const selectedItemValue = selectedItem.value as SearchResult;
  reportSearchEvent( selectedItemValue.ticker );
  navigateToSelectedItem( selectedItemValue );
}

function navigateToSelectedItem( selectedItem: SearchResult ) {
  if ( selectedItem.isStock ) {
    props.stockClickHandler?.( selectedItem.ticker );
  } else if ( selectedItem.isEtf ) {
    props.etfClickHandler?.( selectedItem.ticker );
  } else if ( selectedItem.isSector ) {
    props.sectorClickHandler?.( selectedItem.ticker );
  }
}
</script>
